import { BASE_URL } from "../../constants";
import { storageService } from "../storage.service";
import { store } from "../../redux/store";
import { logout } from "../../redux/slices/authSlice";
import { startLoading, stopLoading } from "../../redux/slices/loadingSlice";

export interface APIResponse<T = unknown> {
  status: number;
  response: string;
  message: string;
  data: T;
}

type HeadersMap = Record<string, string>;
type Query = Record<string, string | number | boolean>;
type Body = Record<string, unknown>;

function formatURL(APIEndpoint: string): string {
  return `${BASE_URL}${APIEndpoint}`;
}

function withQuery(url: string, queryString?: Query): string {
  if (!queryString) {
    return url;
  }

  const params = new URLSearchParams();
  Object.entries(queryString).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });

  const str = params.toString();
  return str ? `${url}?${str}` : url;
}

export function getAPIHeaders(
  additionalHeaders?: HeadersMap
): Promise<HeadersMap> {
  return Promise.resolve({
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
    ...additionalHeaders,
  });
}

export function getAuthHeaders(
  additionalHeaders?: HeadersMap
): Promise<HeadersMap> {
  const token = storageService.getToken();
  return Promise.resolve({
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
    Authorization: `Bearer ${token}`,
    ...additionalHeaders,
  });
}

function handleResponse<T>(res: Response): Promise<APIResponse<T>> {
  return res.json().then((json) => {
    if (
      res.status === 401 ||
      json?.status === 401 ||
      json?.response === "UNAUTHORIZED"
    ) {
      if (store.getState().auth.isAuthenticated) {
        store.dispatch(logout());
      }
      throw new Error(json.message);
    }

    if (!res.ok) {
      throw new Error(json.message);
    }

    return json as APIResponse<T>;
  });
}

const inFlightRequests = new Map<string, Promise<APIResponse<unknown>>>();

function request<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  APIEndpoint: string,
  headers?: HeadersMap,
  queryString?: Query,
  body?: Body,
  options?: { skipLoader?: boolean }
): Promise<APIResponse<T>> {
  const url = withQuery(formatURL(APIEndpoint), queryString);
  const dedupeKey = `${method}:${url}:${body ? JSON.stringify(body) : ""}`;

  const existing = inFlightRequests.get(dedupeKey);
  if (existing) {
    return existing as Promise<APIResponse<T>>;
  }

  if (!options?.skipLoader) {
    store.dispatch(startLoading());
  }

  const getHeaders = headers ? Promise.resolve(headers) : getAPIHeaders();

  const promise = getHeaders
    .then((finalHeaders) =>
      fetch(url, {
        method,
        headers: finalHeaders,
        body: body ? JSON.stringify(body) : undefined,
      }).then(handleResponse<T>)
    )
    .finally(() => {
      inFlightRequests.delete(dedupeKey);
      if (!options?.skipLoader) {
        store.dispatch(stopLoading());
      }
    });

  inFlightRequests.set(dedupeKey, promise as Promise<APIResponse<unknown>>);
  return promise;
}

export function getAuthFormDataHeaders(
  additionalHeaders?: HeadersMap
): Promise<HeadersMap> {
  const token = storageService.getToken();
  return Promise.resolve({
    "ngrok-skip-browser-warning": "true",
    Authorization: `Bearer ${token}`,
    ...additionalHeaders,
  });
}

export function APIPostFormData<T>(
  APIEndpoint: string,
  headers?: HeadersMap,
  body?: FormData
): Promise<APIResponse<T>> {
  store.dispatch(startLoading());

  const url = formatURL(APIEndpoint);
  const getHeaders = headers ? Promise.resolve(headers) : getAuthFormDataHeaders();

  return getHeaders
    .then((finalHeaders) =>
      fetch(url, {
        method: "POST",
        headers: finalHeaders,
        body,
      }).then(handleResponse<T>)
    )
    .finally(() => {
      store.dispatch(stopLoading());
    });
}

const inFlightBlobRequests = new Map<string, Promise<Blob>>();

export function APIGetBlob(
  APIEndpoint: string,
  headers?: HeadersMap,
  queryString?: Query
): Promise<Blob> {
  const url = withQuery(formatURL(APIEndpoint), queryString);

  const existing = inFlightBlobRequests.get(url);
  if (existing) {
    return existing;
  }

  store.dispatch(startLoading());

  const getHeaders = headers ? Promise.resolve(headers) : getAuthHeaders();

  const promise = getHeaders
    .then((finalHeaders) =>
      fetch(url, {
        method: "GET",
        headers: finalHeaders,
      }).then((res) => {
        if (res.status === 401) {
          if (store.getState().auth.isAuthenticated) {
            store.dispatch(logout());
          }
          throw new Error("Unauthorized access. Please login again.");
        }

        if (!res.ok) {
          throw new Error("Failed to download requested file.");
        }

        return res.blob();
      })
    )
    .finally(() => {
      inFlightBlobRequests.delete(url);
      store.dispatch(stopLoading());
    });

  inFlightBlobRequests.set(url, promise);
  return promise;
}

export function APIGet<T>(
  APIEndpoint: string,
  headers?: HeadersMap,
  queryString?: Query,
  body?: Body,
  options?: { skipLoader?: boolean }
): Promise<APIResponse<T>> {
  return request<T>("GET", APIEndpoint, headers, queryString, body, options);
}

export function APIPost<T>(
  APIEndpoint: string,
  headers?: HeadersMap,
  queryString?: Query,
  body?: Body,
  options?: { skipLoader?: boolean }
): Promise<APIResponse<T>> {
  return request<T>("POST", APIEndpoint, headers, queryString, body, options);
}

export function APIPut<T>(
  APIEndpoint: string,
  headers?: HeadersMap,
  queryString?: Query,
  body?: Body
): Promise<APIResponse<T>> {
  return request<T>("PUT", APIEndpoint, headers, queryString, body);
}

export function APIPatch<T>(
  APIEndpoint: string,
  headers?: HeadersMap,
  queryString?: Query,
  body?: Body
): Promise<APIResponse<T>> {
  return request<T>("PATCH", APIEndpoint, headers, queryString, body);
}

export function APIDelete<T>(
  APIEndpoint: string,
  headers?: HeadersMap,
  queryString?: Query,
  body?: Body
): Promise<APIResponse<T>> {
  return request<T>("DELETE", APIEndpoint, headers, queryString, body);
}
